import React from 'react';
import { clsx } from 'clsx';
import { useTranslation, Trans } from 'react-i18next';
import logo from '@/assets/images/common/kotomil-logo.png';
import Tooltip from '@/components/Tooltip';

const INSTAGRAM_ACCOUNT = 'kotomilapp';

function isJapaneseLocale(i18n: { resolvedLanguage?: string; language?: string }): boolean {
    return i18n.resolvedLanguage === 'ja' || Boolean(i18n.language?.startsWith('ja'));
}

function xFollowUrlForLocale(isJa: boolean): string {
    const account = isJa ? 'KotomilApp' : 'kotomilAppEn';
    return `https://x.com/${account}`;
}

function instagramFollowUrl(account: string): string {
    return `https://www.instagram.com/${account}/`;
}

function IconX({ className, size = 16 }: { className?: string; size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden
        >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

function IconInstagram({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
    );
}

function ServiceTooltipBody({ lines }: { lines: string[] }) {
    return (
        <div className="flex flex-col gap-2 text-center leading-relaxed">
            <div>
                {lines.map((line, i) => (
                    <p key={`tooltip-${i}`}>{line}</p>
                ))}
            </div>
        </div>
    );
}

type BrandBlockProps = {
    tooltipLines: string[];
    xFollowUrl: string;
    instagramUrl: string;
};

function FooterAppBrandBlock({ tooltipLines, xFollowUrl, instagramUrl }: BrandBlockProps) {
    return (
        <Tooltip content={<ServiceTooltipBody lines={tooltipLines} />}>
            <div className="flex w-full max-w-xl flex-col items-center gap-3 rounded-xl p-4 text-center transition-colors duration-300 hover:bg-gray-100 cursor-default md:flex-row md:items-center md:justify-center md:gap-4 md:py-2.5 md:px-4 mx-auto">
                <div className="flex flex-col items-center gap-3 text-center md:gap-2">
                    <p className="text-gray-900 text-xs font-bold tracking-widest leading-relaxed">
                        <Trans i18nKey="service.catchphrase" ns="footer" components={{ br: <br /> }} />
                    </p>
                    <div className="flex w-fit items-center gap-3">
                        <img src={logo} alt="Kotomil" className="h-8 w-auto" />
                        <span className="text-2xl font-bold tracking-wider text-gray-900">Kotomil</span>
                    </div>
                </div>
                <div className="flex shrink-0 justify-center gap-3">
                    <Tooltip content="X (Twitter)">
                        <a
                            href={xFollowUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-all duration-300 hover:border-black hover:bg-black hover:text-white"
                            aria-label="X (Twitter)"
                        >
                            <IconX size={16} />
                        </a>
                    </Tooltip>
                    <Tooltip content="Instagram">
                        <a
                            href={instagramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-all duration-300 hover:border-transparent hover:bg-gradient-to-tr hover:from-yellow-400 hover:via-red-500 hover:to-purple-500 hover:text-white"
                            aria-label="Instagram"
                        >
                            <IconInstagram size={18} />
                        </a>
                    </Tooltip>
                </div>
            </div>
        </Tooltip>
    );
}

/**
 * ログイン後シェル用フッター（ブランド＋コピーライトのみ）。
 * サポート・法令・シェアは設定画面 {@link ../pages/Settings.tsx} 下部へ集約。
 * 公開用 {@link ./Footer.tsx} のブランド表現は揃える。
 */
const FooterApp: React.FC = () => {
    const { t, i18n } = useTranslation('footer');
    const currentYear = new Date().getFullYear();

    const xFollowUrl = xFollowUrlForLocale(isJapaneseLocale(i18n));
    const igUrl = instagramFollowUrl(INSTAGRAM_ACCOUNT);

    const tooltipLines = t('service.tooltipLines', {
        returnObjects: true,
    }) as string[];

    return (
        <footer
            className={clsx(
                'mt-auto w-full border-t border-gray-200 bg-gray-50 pt-12 text-gray-800 md:pt-7',
                'pb-8 max-lg:pb-[calc(var(--kotomil-bottom-nav-height,6rem)+env(safe-area-inset-bottom,0px)+0.75rem)] lg:pb-5',
            )}
        >
            <div className="max-w-6xl mx-auto px-8">
                <div className="mb-5 flex justify-center">
                    <FooterAppBrandBlock tooltipLines={tooltipLines} xFollowUrl={xFollowUrl} instagramUrl={igUrl} />
                </div>

                <div className="border-t border-gray-200 pt-6 text-center md:pt-4">
                    <p className="text-xs text-gray-500 font-medium">
                        {t('copyright', { year: currentYear })}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default FooterApp;
